// src/features/organizations/organizationsApiSlice.js
import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const organizationsAdapter = createEntityAdapter({})

const initialState = organizationsAdapter.getInitialState()

export const organizationsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getOrganizations: builder.query({
            query: () => ({
                url: '/organizations',
                validateStatus: (response, result) => {

                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedOrganizations = responseData.map(organization => {
                    organization.id = organization.organization_id
                    return organization
                });
                return organizationsAdapter.setAll(initialState, loadedOrganizations)
            },
            providesTags: (result, error, arg) => {
                if (result?.organization_id) {
                    return [
                        { type: 'Organization', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Organization', id }))
                    ]
                } else return [{ type: 'Organization', id: 'LIST' }]
            }
        }),
        addNewOrganization: builder.mutation({
            query: initialOrganizationData => ({
                url: '/organizations',
                method: 'POST',
                body: {
                    ...initialOrganizationData,
                }
            }),
            invalidatesTags: [
                { type: 'Organization', id: "LIST" }
            ]
        }),
        getMinimalOrganizations: builder.query({
            query: () => ({
                url: '/organizations/minimal',
                validateStatus: (response, result) => response.status === 200 && !result.isError,
            }),
            transformResponse: responseData => {
                const loadedOrganizations = responseData.map(organization => ({
                    ...organization,
                    id: organization.organization_id, // Normalize ID
                }));
                return organizationsAdapter.setAll(initialState, loadedOrganizations);
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Organization', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Organization', id })),
                    ];
                } else return [{ type: 'Organization', id: 'LIST' }];
            },
        }),
        getOrganizationByOrganizationId: builder.query({
            query: (organizationId) => `/organizations/findByOrgId?organizationId=${organizationId}`,
            transformResponse: responseData => {
                responseData.id = responseData.organization_id;
                return responseData;
            },
            providesTags: (result, error, organizationId) => [{ type: 'Organization', organizationId }],
        }),
        updateOrganization: builder.mutation({
            query: initialOrganizationData => ({
                url: '/organizations',
                method: 'PATCH',
                body: {
                    ...initialOrganizationData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ]
        }),
        updateOrgDeliveryFormat: builder.mutation({
            query: ({ id, delivery_format }) => ({
                url: `/organizations/${id}/delivery-format`,
                method: 'PATCH',
                body: { delivery_format },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
                { type: 'Organization', id: 'LIST' },
            ],
        }),
        updateOrgS3DeliveryOverrides: builder.mutation({
            query: ({ id, s3_delivery_overrides }) => ({
                url: `/organizations/${id}/s3-delivery-overrides`,
                method: 'PATCH',
                body: { s3_delivery_overrides },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
                { type: 'Organization', id: 'LIST' },
            ],
        }),
        updateAdcConfig: builder.mutation({
            query: ({ id, adcConfig }) => ({
                url: `/organizations/${id}/adc_config`, // Ensure URL correctness
                method: 'PATCH',
                body: adcConfig,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        deleteOrganization: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ]
        }),
        addOrganizationS3Config: builder.mutation({
            query: ({ id, s3Config }) => ({
                url: `/organizations/${id}/s3_config`,
                method: 'POST',
                body: { s3_config: s3Config },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        enableIntentProduct: builder.mutation({
            query: ({ id, enable_intent_product, aws_account_id }) => ({
                url: `/organizations/${id}/enable-intent`,
                method: 'PATCH',
                body: { id, enable_intent_product, aws_account_id },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
                { type: 'Organization', id: "LIST" }
            ],
        }),
        updateWhitelabelConfig: builder.mutation({
            query: ({ id, whitelabel_config }) => ({
                url: `/organizations/${id}/whitelabel_config`,
                method: 'PATCH',
                body: { whitelabel_config },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        getSampleADCOrganizations: builder.query({
            query: () => '/organizations/sampleADC',
            providesTags: ['Organization'],
        }),
        enableAdcWithDelivery: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/enable-adc-with-delivery`,
                method: 'PATCH',
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        enableAdcOnly: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/enable-adc-only`,
                method: 'PATCH',
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        enableReleaseProduct: builder.mutation({
            query: ({ id, product }) => ({
                url: `/organizations/${id}/enable-release-product`,
                method: 'PATCH',
                body: { product },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
                { type: 'Organization', id: 'LIST' },
            ],
        }),

        disableReleaseProduct: builder.mutation({
            query: ({ id, product }) => ({
                url: `/organizations/${id}/disable-release-product`,
                method: 'PATCH',
                body: { product },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
                { type: 'Organization', id: 'LIST' },
            ],
        }),
        getReleaseProductDeliveries: builder.query({
            query: ({ id, product, data_asset_id }) => {
                const params = new URLSearchParams();

                if (product) params.set('product', product);
                if (data_asset_id) params.set('data_asset_id', data_asset_id);

                return {
                    url: `/organizations/${id}/release-product-deliveries?${params.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id },
            ],
        }),
        connectExistingBucket: builder.mutation({
            query: ({ id, bucketNameOrUri }) => ({
                url: `/organizations/${id}/connect_existing_bucket`,
                method: 'POST',
                body: { bucketNameOrUri },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Organization', id: arg.id }
            ],
        }),
        enableApisProduct: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/enable-apis-product`,
                method: 'PATCH',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Organization', id: arg.id }],
        }),

        createOrgApiKey: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/apis/apikeys`,
                method: 'POST',
            }),
            // Optionally invalidate org to reflect new product state or stored key metadata later
            invalidatesTags: (result, error, arg) => [{ type: 'Organization', id: arg.id }],
        }),
        setOrgApiPlan: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/organizations/${id}/apis/plan`,
                method: 'POST',
                body
            }),
            invalidatesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }]
        }),
        deleteOrgApiPlan: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/apis/plan`,
                method: 'DELETE',
            }),
            invalidatesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }]
        }),
        rotateOrgApiKey: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/apis/apikeys/rotate`,
                method: 'POST',
            }),
            invalidatesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }]
        }),

        revokeOrgApiKey: builder.mutation({
            query: ({ id }) => ({
                url: `/organizations/${id}/apis/apikeys`,
                method: 'DELETE',
            }),
            invalidatesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }]
        }),

        getOrgApiUsage: builder.query({
            query: ({ id, start, end }) => ({
                url: `/organizations/${id}/apis/usage?start=${start}&end=${end}`,
            }),
            providesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }]
        }),
        getOrgMpDaily: builder.query({
            query: ({ id, start, end }) => ({
                url: `/organizations/${id}/apis/mp/daily?start=${start}&end=${end}`,
            }),
            providesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }],
        }),

        getOrgMpJobs: builder.query({
            query: ({ id, date }) => ({
                url: `/organizations/${id}/apis/mp/jobs?date=${date}`,
            }),
            providesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }],
        }),
        getApiOverview: builder.query({
            query: ({ start, end }) => ({
                url: `/organizations/__api_admin/overview?start=${start}&end=${end}`,
            }),
        }),
        getApisUsageSummary: builder.query({
            query: ({ range }) => ({
                url: `/organizations/__api_admin/usage/summary?range=${range}`,
            }),
        }),
        getApisChargesFeed: builder.query({
            query: ({ range, q }) => {
                const qs = new URLSearchParams();
                if (range) qs.set('range', range);
                if (q) qs.set('q', q);
                return { url: `/organizations/__api_admin/usage/charges?${qs.toString()}` };
            },
        }),
        setOrgApiDelivery: builder.mutation({
            query: ({ id, deliver_to_s3 }) => ({
                url: `/organizations/${id}/apis/delivery`,
                method: 'PATCH',
                body: { deliver_to_s3 },
            }),
            invalidatesTags: (r, e, arg) => [{ type: 'Organization', id: arg.id }],
        }),

    }),
})

export const {
    useGetOrganizationsQuery,
    useGetOrganizationByOrganizationIdQuery,
    useGetMinimalOrganizationsQuery,
    useAddNewOrganizationMutation,
    useUpdateOrganizationMutation,
    useUpdateOrgDeliveryFormatMutation,
    useUpdateOrgS3DeliveryOverridesMutation,
    useUpdateAdcConfigMutation,
    useDeleteOrganizationMutation,
    useAddOrganizationS3ConfigMutation,
    useEnableIntentProductMutation,
    useUpdateWhitelabelConfigMutation,
    useGetSampleADCOrganizationsQuery,
    useEnableAdcWithDeliveryMutation,
    useEnableAdcOnlyMutation,
    useEnableReleaseProductMutation,
    useDisableReleaseProductMutation,
    useGetReleaseProductDeliveriesQuery,
    useConnectExistingBucketMutation,
    useEnableApisProductMutation,
    useCreateOrgApiKeyMutation,
    useSetOrgApiPlanMutation,
    useDeleteOrgApiPlanMutation,
    useRotateOrgApiKeyMutation,
    useRevokeOrgApiKeyMutation,
    useGetOrgApiUsageQuery,
    useGetApiOverviewQuery,
    useGetApisUsageSummaryQuery,
    useGetApisChargesFeedQuery,
    useGetOrgMpDailyQuery,
    useGetOrgMpJobsQuery,
    useSetOrgApiDeliveryMutation
} = organizationsApiSlice

// returns the query result object
export const selectOrganizationsResult = organizationsApiSlice.endpoints.getOrganizations.select()

// creates memoized selector
const selectOrganizationsData = createSelector(
    selectOrganizationsResult,
    organizationsResult => organizationsResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllOrganizations,
    selectById: selectOrganizationById,
    selectIds: selectOrganizationIds
    // Pass in a selector that returns the organizations slice of state
} = organizationsAdapter.getSelectors(state => selectOrganizationsData(state) ?? initialState)